import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import WebAppService from '../services/WebAppService';
import {
  ProjectLinkType,
  ProjectLinkTypesResponse,
  ProjectPrioritiesResponse,
  ProjectPriority,
  ProjectStatus,
  ProjectStatusesResponse,
  TaskDepartment,
  TaskDepartmentsResponse,
  TaskLinkType,
  TaskLinkTypesResponse,
  TaskPrioritiesResponse,
  TaskPriority,
  TaskStatus,
  TaskStatusesResponse,
  TaskType,
  TaskTypesResponse,
} from '../types';

type EnumsError = Error | null;

interface EnumsState {
  // Task enums
  taskStatuses: TaskStatus[];
  taskTypes: TaskType[];
  taskPriorities: TaskPriority[];
  taskDepartments: TaskDepartment[];
  taskLinkTypes: TaskLinkType[];

  // Project enums
  projectStatuses: ProjectStatus[];
  projectPriorities: ProjectPriority[];
  projectLinkTypes: ProjectLinkType[];

  // Loading / error state
  loaded: boolean;
  loading: boolean;
  error: EnumsError;

  fetchAllEnums: (force?: boolean) => Promise<void>;
  resetEnums: () => void;
}

const initialState = {
  // Task enums
  taskStatuses: [] as TaskStatus[],
  taskTypes: [] as TaskType[],
  taskPriorities: [] as TaskPriority[],
  taskDepartments: [] as TaskDepartment[],
  taskLinkTypes: [] as TaskLinkType[],

  // Project enums
  projectStatuses: [] as ProjectStatus[],
  projectPriorities: [] as ProjectPriority[],
  projectLinkTypes: [] as ProjectLinkType[],

  // Loading / error state
  loaded: false,
  loading: false,
  error: null as EnumsError,
};

const asStringArray = <T extends string>(value: unknown): T[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => typeof item === 'string');
};

const useEnumsStore = create<EnumsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Fetch all enums in parallel. Only fetches once —
       * subsequent calls are no-ops unless `force` is true.
       */
      fetchAllEnums: async (force = false) => {
        const { loaded, loading } = get();

        // Skip if already loaded or currently loading (unless forced)
        if ((loaded || loading) && !force) return;

        set({ loading: true, error: null });

        try {
          const [
            taskStatusesResponse,
            taskTypesResponse,
            taskPrioritiesResponse,
            taskDepartmentsResponse,
            taskLinkTypesResponse,
            projectStatusesResponse,
            projectPrioritiesResponse,
            projectLinkTypesResponse,
          ] = await Promise.all([
            WebAppService.getTaskStatuses() as Promise<TaskStatusesResponse>,
            WebAppService.getTaskTypes() as Promise<TaskTypesResponse>,
            WebAppService.getTaskPriorities() as Promise<TaskPrioritiesResponse>,
            WebAppService.getTaskDepartments() as Promise<TaskDepartmentsResponse>,
            WebAppService.getTaskLinkTypes() as Promise<TaskLinkTypesResponse>,
            WebAppService.getProjectStatuses() as Promise<ProjectStatusesResponse>,
            WebAppService.getProjectPriorities() as Promise<ProjectPrioritiesResponse>,
            WebAppService.getProjectLinkTypes() as Promise<ProjectLinkTypesResponse>,
          ]);

          set({
            taskStatuses: asStringArray<TaskStatus>(
              taskStatusesResponse?.data?.available_statuses
            ),
            taskTypes: asStringArray<TaskType>(
              taskTypesResponse?.data?.available_task_types
            ),
            taskPriorities: asStringArray<TaskPriority>(
              taskPrioritiesResponse?.data?.available_priorities
            ),
            taskDepartments: asStringArray<TaskDepartment>(
              taskDepartmentsResponse?.data?.available_task_departments
            ),
            taskLinkTypes: asStringArray<TaskLinkType>(
              taskLinkTypesResponse?.data?.available_link_types
            ),
            projectStatuses: asStringArray<ProjectStatus>(
              projectStatusesResponse?.data?.available_statuses
            ),
            projectPriorities: asStringArray<ProjectPriority>(
              projectPrioritiesResponse?.data?.available_priorities
            ),
            projectLinkTypes: asStringArray<ProjectLinkType>(
              projectLinkTypesResponse?.data?.available_link_types
            ),

            loaded: true,
            loading: false,
          });
        } catch (error) {
          console.error('Failed to fetch enums:', error);
          set({
            error: error instanceof Error ? error : new Error(String(error)),
            loading: false,
          });
        }
      },

      resetEnums: () => set({ ...initialState }),
    }),
    { name: 'EnumsStore' }
  )
);

export default useEnumsStore;
